import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export const SupportKbImport: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState('')
  const [dragActive, setDragActive] = useState(false)

  const invalidateKb = () => {
    queryClient.invalidateQueries({ queryKey: ['support-module-categories'] })
    queryClient.invalidateQueries({ queryKey: ['support-module-articles'] })
  }

  const importMutation = useMutation({
    mutationFn: async (markdown: string) => {
      const response = await api.post('/support/articles/import', { markdown })
      return response.data.data as { created: number; updated: number }
    },
    onSuccess: (data) => {
      setUploadSuccess(t('supportModule.importArticlesSuccess', { created: data.created, updated: data.updated }))
      setUploadError('')
      invalidateKb()
      setTimeout(() => setUploadSuccess(''), 5000)
    },
    onError: (error: any) => {
      setUploadError(error.response?.data?.error || t('supportModule.uploadError'))
      setUploadSuccess('')
    },
  })

  const importMarkdown = (markdown: string) => {
    setIsUploading(true)
    importMutation.mutate(markdown, {
      onSettled: () => {
        setIsUploading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
    })
  }

  const handleFile = (file: File) => {
    setUploadError('')
    setUploadSuccess('')

    const lowerName = file.name.toLowerCase()
    if (!lowerName.endsWith('.md') && !lowerName.endsWith('.markdown')) {
      setUploadError(t('supportModule.uploadOnlyMd'))
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const markdown = event.target?.result as string
      if (markdown) importMarkdown(markdown)
    }
    reader.readAsText(file)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  return (
    <div className="space-y-3">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-[#ff6600] bg-[#ff6600]/5'
            : 'border-white/20 hover:border-white/40'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,text/markdown,text/plain"
          onChange={handleChange}
          className="hidden"
        />
        <div className="text-3xl mb-2">📄</div>
        <p className="text-gray-300 text-sm mb-1">
          {t('supportModule.dragDropMd')}
        </p>
        <p className="text-gray-500 text-xs mb-3">
          {t('supportModule.orClickToBrowse')}
        </p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
        >
          {isUploading
            ? t('supportModule.uploading')
            : t('supportModule.selectFile')}
        </button>
      </div>

      {uploadSuccess && (
        <p className="text-green-400 text-sm bg-green-400/10 px-3 py-2 rounded-lg">
          {uploadSuccess}
        </p>
      )}
      {uploadError && (
        <p className="text-red-400 text-sm bg-red-400/10 px-3 py-2 rounded-lg">
          {uploadError}
        </p>
      )}
    </div>
  )
}
